import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import {
    DataLoaderUpliftHelper,
    IDataLoaderUpliftHelperOptions,
} from 'src/app/services/data-loader/data-loader-uplift.helper';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import {
    DatasourceColumnId,
    generateTableFullPath,
} from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { StringUtilsHelper } from 'src/app/utils/string-utils-helper';
import { BiStgSkanDecodedPostbacks } from '../sql-maps/bistg-skan-decoded-postbacks-datasource-sql-map';

export interface SkanPostbacksDataLoaderFactoryOptions
    extends
        ICreatedInstanceId,
        IGcpProjectId,
        IQueryCacheService,
        IDataLoaderUpliftHelperOptions {}

export class SkadNetworkDataLoaderFactory extends Factory<
    DataLoader,
    SkanPostbacksDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'SkadNetworkDataLoaderFactory';

    getInstance(options: SkanPostbacksDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );
                console.log('SkadNetworkDataLoaderFactory', queryContext);

                const ignoredContextColumnIds: DatasourceColumnId[] = [];

                const datasourceSqlMap = BiStgSkanDecodedPostbacks.sqlMap;

                const skadNetworkDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        datasourceSqlMap.columns
                    );

                const skadNetworkDsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(
                        skadNetworkDsColumns.missing
                    );

                skadNetworkDsColumns.present =
                    skadNetworkDsColumns.present.filter(
                        (col) => !ignoredContextColumnIds.includes(col.id)
                    );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(
                        skadNetworkDsColumns.missing
                    );

                const skanSQL =
                    BiStgSkanDecodedPostbacks.sqlStatementsCompiler.compileSQLStatements(
                        skadNetworkDsColumns.present,
                        queryContext.settings,
                        options
                    );

                const pivotValues = () => {
                    return ['NULL']
                        .concat(Array.from({ length: 64 }, (_, i) => `'${i}'`))
                        .join(', ');
                };

                const nonZeroValues = () => {
                    return Array.from(
                        { length: 63 },
                        (_, i) => `COALESCE(cnt_${i + 1}, 0)`
                    ).join(' + ');
                };

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH base AS (
  SELECT
    ${StringUtilsHelper.suffixIfNotNull(
        skanSQL.splitSelectAsSeparateColumns,
        ','
    )}
    CAST(conversion_value AS STRING) AS cv_group
    , SUM(postbacks_count) AS cnt
    , SUM(IF(conversion_value >= 16, postbacks_count, 0)) AS payers
    , SUM(IF(conversion_value IS NOT NULL
                               AND conversion_value NOT IN (0,16,32,48),
                               postbacks_count, 0)) AS active
    
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      BiStgSkanDecodedPostbacks.sqlMap.dataset,
      BiStgSkanDecodedPostbacks.sqlMap.tableId
  )}\` ds
  ${skanSQL.join}
  ${skanSQL.where}
  GROUP BY ALL
)

, pivoted AS (SELECT
  *
FROM base 
PIVOT (
    SUM(cnt) AS cnt, SUM(payers) AS payers, SUM(active) AS active FOR cv_group IN (${pivotValues()})
  )
ORDER BY 1, 2, 3)

SELECT
  *,
  (
    ${nonZeroValues()}
  ) AS neq_0
FROM pivoted
ORDER BY 1;
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );

                cancellationToken.throwIfCancellationRequested();
                var data = [...queryResponse.result];
                bq2echartsformat(data);

                DataLoaderUpliftHelper.processUpliftMetrics(
                    options,
                    queryContext,
                    data
                );

                this.emitter.emit({
                    data,
                    queryContext,
                    status: skadNetworkDsColumnsStatus.pendingDataLoaderResultStatus,
                    statusMessage:
                        skadNetworkDsColumnsStatus.pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
